import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, Globe } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useLocalizedPath, stripLangPrefix } from "@/hooks/useLocalizedPath";
import logoImage from "@/assets/geonova-logo.png";

const SECTION_IDS = ["hero", "approach", "services", "cases", "pricing", "faq", "contact"];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState<string>("hero");
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { localePath } = useLocalizedPath();

  useEffect(() => {
    let ticking = false;
    let cachedDocHeight = document.documentElement.scrollHeight - window.innerHeight;
    
    const updateDocHeight = () => {
      cachedDocHeight = document.documentElement.scrollHeight - window.innerHeight;
    };
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollTop = window.scrollY;
          setIsScrolled(scrollTop > 20);
          const progress = cachedDocHeight > 0 ? (scrollTop / cachedDocHeight) * 100 : 0;
          setScrollProgress(progress);
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", updateDocHeight, { passive: true });
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", updateDocHeight);
    };
  }, []);

  const isHomePage = /^\/(fr|en)\/?$/.test(location.pathname);

  // IntersectionObserver for active section detection on homepage
  useEffect(() => {
    if (!isHomePage) return;

    const sectionPositions = new Map<string, number>();

    const updateActive = () => {
      const scrollY = window.scrollY + window.innerHeight * 0.35;
      let current = SECTION_IDS[0];
      for (const id of SECTION_IDS) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= scrollY) {
          current = id;
        }
      }
      if (current !== activeSection) {
        setActiveSection(current);
        if (current === "hero") {
          window.history.replaceState(null, "", window.location.pathname);
        } else {
          window.history.replaceState(null, "", `${window.location.pathname}#${current}`);
        }
      }
    };

    window.addEventListener("scroll", updateActive, { passive: true });
    updateActive();

    return () => window.removeEventListener("scroll", updateActive);
  }, [isHomePage, activeSection]);

  const scrollToSection = (id: string) => {
    const updateHash = () => {
      if (id === "hero") {
        window.history.replaceState(null, "", window.location.pathname);
      } else {
        window.history.replaceState(null, "", `${window.location.pathname}#${id}`);
      }
    };

    if (!isHomePage) {
      navigate(localePath("") + (id === "hero" ? "" : `#${id}`));
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      updateHash();
    }
    setIsMobileMenuOpen(false);
  };

  const handleNavigation = (path: string) => {
    navigate(localePath(path));
    setIsMobileMenuOpen(false);
  };

  const switchLanguage = () => {
    const newLang = language === "fr" ? "en" : "fr";
    setLanguage(newLang);
    // Navigate to the same page but with new lang prefix
    const basePath = stripLangPrefix(location.pathname);
    navigate(`/${newLang}${basePath === "/" ? "" : basePath}`);
  };

  const navItems = [
    { label: t("nav.home"), id: "hero" },
    { label: t("nav.method"), id: "approach" },
    { label: t("nav.services"), id: "services" },
    { label: t("nav.cases"), id: "cases" },
    { label: t("nav.pricing"), id: "pricing" },
    { label: t("nav.faq"), id: "faq" },
    { label: t("nav.blog"), path: "/blog" },
    { label: t("nav.contact"), id: "contact" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-slate-200/80 shadow-lg shadow-slate-200/20"
          : "bg-white/90 backdrop-blur-md border-b border-slate-100"
      }`}
    >
      <div 
        className="absolute bottom-0 left-0 h-[2px] bg-gradient-to-r from-sky-400 via-blue-500 to-blue-700 transition-all duration-100 shadow-[0_0_8px_rgba(59,130,246,0.4)]" 
        style={{ width: `${scrollProgress}%` }} 
      />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20 gap-8">
          <div className="flex-shrink-0 -ml-2">
            <button
              onClick={() => scrollToSection("hero")}
              className="flex flex-col hover:opacity-80 transition-opacity group"
            >
              <span className="text-xl lg:text-2xl font-display font-bold flex items-center">
                <span className="text-slate-700">G</span>
                <span className="relative inline-flex items-center justify-center h-8 lg:h-9 w-8 lg:w-9 -mx-[0.2rem]">
                  <img 
                    src={logoImage} 
                    alt={language === "fr" ? "Gogenia.ai - Agence de référencement IA et GEO" : "Gogenia.ai - AI Referencing & GEO Agency"} 
                    className="h-full w-full object-contain filter brightness-0 invert-[.35] sepia-[.1] saturate-[0] hue-rotate-[0deg]" 
                    loading="eager"
                  />
                </span>
                <span className="text-slate-700">GENIA</span>
                <span className="bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">.AI</span>
              </span>
              <span className="hidden lg:block text-[11px] text-slate-500 font-medium tracking-wide -mt-1.5">
                Be the reference generative AI recommends
              </span>
            </button>
          </div>

          <nav className="hidden lg:flex items-center space-x-7">
            {navItems.map((item) => {
              const isActive = item.path
                ? location.pathname.includes(item.path)
                : isHomePage && activeSection === item.id;
              return (
                <button
                  key={item.label}
                  onClick={() => item.path ? handleNavigation(item.path) : scrollToSection(item.id!)}
                  className={`text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive
                      ? "text-sky-500 font-semibold"
                      : "text-slate-600 hover:text-sky-500"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
            <button
              onClick={switchLanguage}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-300 hover:border-sky-400 hover:bg-sky-50 transition-all text-xs font-medium text-slate-500 hover:text-sky-600"
            >
              <Globe className="h-3.5 w-3.5" />
              <span>{language.toUpperCase()}</span>
            </button>
            <Button
              onClick={() => scrollToSection("contact")}
              size="sm"
              className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-white font-semibold px-4 whitespace-nowrap shadow-lg shadow-sky-500/25 rounded-full"
            >
              {t("cta.audit")}
            </Button>
          </div>

          <button
            className="lg:hidden p-2 text-slate-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-slate-200/80">
          <nav className="container mx-auto px-4 py-4 flex flex-col space-y-4">
            {navItems.map((item) => {
              const isActive = item.path
                ? location.pathname.includes(item.path)
                : isHomePage && activeSection === item.id;
              return (
                <button
                  key={item.label}
                  onClick={() => item.path ? handleNavigation(item.path) : scrollToSection(item.id!)}
                  className={`text-sm font-medium transition-colors text-left ${
                    isActive
                      ? "text-sky-500 font-semibold"
                      : "text-slate-600 hover:text-sky-500"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
            <div className="flex items-center gap-2 bg-slate-100 rounded-full p-1">
              <button
                onClick={() => {
                  setLanguage("fr");
                  const basePath = stripLangPrefix(location.pathname);
                  navigate(`/fr${basePath === "/" ? "" : basePath}`);
                  setIsMobileMenuOpen(false);
                }}
                className={`flex-1 px-3 py-2 rounded-full text-xs font-medium transition-all ${
                  language === "fr"
                    ? "bg-gradient-to-r from-sky-500 to-blue-600 text-white"
                    : "text-slate-500"
                }`}
              >
                Français
              </button>
              <button
                onClick={() => {
                  setLanguage("en");
                  const basePath = stripLangPrefix(location.pathname);
                  navigate(`/en${basePath === "/" ? "" : basePath}`);
                  setIsMobileMenuOpen(false);
                }}
                className={`flex-1 px-3 py-2 rounded-full text-xs font-medium transition-all ${
                  language === "en"
                    ? "bg-gradient-to-r from-sky-500 to-blue-600 text-white"
                    : "text-slate-500"
                }`}
              >
                English
              </button>
            </div>
            <Button
              onClick={() => scrollToSection("contact")}
              className="bg-gradient-to-r from-sky-500 to-blue-600 text-white font-semibold w-full rounded-full"
            >
              {t("cta.audit")}
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
